import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { CaretDown, Buildings, Check, Plus } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

// Workspace switcher dropdown — top-right of the app shell.
// Shows the active workspace + plan badge, lets the user switch between
// workspaces they belong to, and opens the "Create New Workspace" dialog.
export const WorkspaceSwitcher = () => {
  const { activeWorkspace, activePlan, myWorkspaces, switchWorkspace } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!activeWorkspace) return null;

  const planColor = {
    free: '#94a3b8',
    starter: '#3b82f6',
    pro: '#7c3aed',
    business: '#1a2e4a',
  }[activePlan?.code] || '#64748b';

  return (
    <div ref={wrapRef} className="relative" data-testid="workspace-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold border bg-white hover:bg-gray-50 transition"
        style={{ borderColor: '#e5e7eb' }}
        data-testid="workspace-switcher-trigger"
      >
        <Buildings size={16} weight="duotone" />
        <span className="truncate max-w-[180px]" title={activeWorkspace.name}>
          {activeWorkspace.name}
        </span>
        <span
          className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-white"
          style={{ background: planColor }}
        >
          {activePlan?.name || activeWorkspace.plan_code}
        </span>
        <CaretDown size={14} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-xl border z-50 overflow-hidden"
          style={{ borderColor: '#e5e7eb' }}
          data-testid="workspace-switcher-menu"
        >
          <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-500 border-b" style={{ borderColor: '#f1f5f9' }}>
            Your Workspaces
          </div>
          <div className="max-h-72 overflow-y-auto">
            {myWorkspaces.map((ws) => {
              const isActive = ws.id === activeWorkspace.id;
              return (
                <button
                  key={ws.id}
                  type="button"
                  onClick={() => { switchWorkspace(ws.id); setOpen(false); }}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2 transition ${isActive ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                  data-testid={`workspace-item-${ws.id}`}
                >
                  <Buildings size={14} weight="duotone" className="text-gray-500" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate" style={{ color: '#1a2e4a' }}>{ws.name}</div>
                    <div className="text-[10px] text-gray-500 capitalize">{ws.plan_code} · {ws.status}</div>
                  </div>
                  {isActive && <Check size={14} className="text-blue-600" />}
                </button>
              );
            })}
            {myWorkspaces.length === 0 && (
              <div className="px-3 py-4 text-xs text-center text-gray-500">No workspaces yet.</div>
            )}
          </div>
          <div className="border-t" style={{ borderColor: '#f1f5f9' }}>
            <button
              type="button"
              onClick={() => { setOpen(false); navigate('/workspaces/new'); }}
              className="w-full text-left px-3 py-2 flex items-center gap-2 text-sm font-semibold text-blue-700 hover:bg-blue-50 transition"
              data-testid="workspace-create-btn"
            >
              <Plus size={14} /> Create New Workspace
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); navigate('/billing'); }}
              className="w-full text-left px-3 py-2 flex items-center gap-2 text-xs text-gray-600 hover:bg-gray-50 transition"
              data-testid="workspace-billing-link"
            >
              💳 Billing &amp; Plans
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkspaceSwitcher;
